import React, { useEffect, useRef } from 'react';
import { Divider } from '@abyss/web/ui/Divider';
import { Flex } from '@abyss/web/ui/Flex';
import { Card } from '@abyss/web/ui/Card';
import { Label } from '@abyss/web/ui/Label';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { styled } from '@abyss/web/tools/styled';
import { HeaderController } from './Header.Controller';
import { Text } from '@abyss/web/ui/Text';
import { Layout } from '@abyss/web/ui/Layout';
import { Brandmark } from '@abyss/web/ui/Brandmark';
import { NavMenuPrimitives } from '@abyss/web/ui/NavMenu';
import { isLeftMenuEnabled } from '@src/config/BoilerplateConfig';
import { useMenu } from './useMenu';
import { DynamicMenuList } from './DynamicMenuList';
import { Avatar } from '@abyss/web/ui/Avatar';
import { getInitials } from './Header.Controller';

const StyledLogoutCard = styled(Card, {
  position: 'relative',
});

const StyledUserFlex = styled(Flex, {
  cursor: 'pointer',
});

const StyledUserLabel = styled(Label, {
  cursor: 'pointer',
  margin: '6px',
  fontSize: 'clamp(0.75rem, 5vw, 1rem)',
});

const CenterMenuWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
  '@media (max-width: 768px)': {
    width: '100%',
    order: 3,
  },
});

const StyledMenuContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  pointerEvents: 'auto',
  position: 'relative',
});

const VerticalDividerContainer = styled('div', {
  height: 'clamp(30px, 4vw, 60px)',
});

const ProfileIconContainer = styled('div', {
  margin: '6px',
});

const MenuToggle = styled('div', {
  marginTop: '5px',
  alignSelf: 'center',
});

const TransparentAvatar = styled(Avatar, {
  backgroundColor: 'transparent !important',

});

type HeaderProps = {
  onMobileMenuClick?: () => void;
};

export const Header: React.FC<HeaderProps> = ({ onMobileMenuClick }) => {
  const [{ user, openLogout, setOpenLogout, Action, isActorAdmin }] = HeaderController();
  const leftMenuEnabled = isLeftMenuEnabled();
  const { menuItems, loading, error } = useMenu();

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (openLogout) {
        setOpenLogout(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenLogout(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openLogout, setOpenLogout]);

  return (
    <Flex
      direction="row"
      justify="space-between"
      alignContent="center"
      alignItems="center"
      className="menu-header-container"
      data-testid="header"
      css={{
        position: 'relative',
        backgroundColor: '#fbf9f4',
        paddingTop: 'clamp(8px, 1.70vw, 22px)',
        paddingBottom: 'clamp(8px, 1.70vw, 22px)',
        paddingLeft: '2rem',
        paddingRight: '2rem',
        flexWrap: 'wrap',
        ...(leftMenuEnabled && { marginLeft: '60px' }),
        minHeight: 'fit-content',
        '@media (max-width: 960px)': {
          flexDirection: 'column',
          gap: '0.5rem',
        },
        '@media (max-width: 768px)': {
          ...(leftMenuEnabled && { marginLeft: '0' }),
        },
      }}
    >
      {/* Left Section */}
      <Flex
        direction="row"
        className="menu-header-left"
        css={{
          flexShrink: 1,
          maxHeight: '60px',
        }}
      >
        {leftMenuEnabled && (
          <MenuToggle data-testid="menu-toggle" className="menu-small-screen">
            <IconSymbol
              icon="menu"
              size="24px"
              color="$primary1"
              onClick={onMobileMenuClick}
              css={{ 'abyss-icon': { cursor: 'pointer' } }}
            />
          </MenuToggle>
        )}
        <Layout.Group css={{ flexShrink: 1 }}>
          <Brandmark
            brand="optum"
            size="$sm"
            affiliate="optum"
            variant="lockup"
            color="orange"
            css={{ marginTop: '0.4rem' }}
          />
          <VerticalDividerContainer>
            <Divider orientation="vertical" width={1} />
          </VerticalDividerContainer>
          <Text
            fontWeight="bold"
            data-testid="header-title"
            css={{ fontSize: 'clamp(0.875rem, calc(0.5rem + 2vw), 1.05rem)' }}
          >
            Customer Technology Experience
          </Text>
        </Layout.Group>
      </Flex>

      {/* Center Menu (Dynamic) */}
      <CenterMenuWrapper>
        <StyledMenuContainer>
          <NavMenuPrimitives.Root zIndex={103} enableOutsideScroll={false}>
            <NavMenuPrimitives.List>
              <DynamicMenuList
                menuItems={menuItems}
                loading={loading}
                error={error}
              />
            </NavMenuPrimitives.List>
            <NavMenuPrimitives.Viewport />
          </NavMenuPrimitives.Root>
        </StyledMenuContainer>
      </CenterMenuWrapper>

      {/* Right Section (Profile) */}
      <Flex
        justify="flex-end"
        alignContent="center"
        alignItems="center"
        css={{
          '@media (max-width: 768px)': {
            width: '100%',
            justifyContent: 'flex-end',
            order: 1,
          },
        }}
      >
        <StyledUserFlex
          data-testid="user-profile"
          css={{ maxHeight: '40px', padding: '0.25rem' }}
          onClick={() => {
            setOpenLogout(!openLogout);
          }}
        >
          {/* <StyledUserLabel data-testid="profile-username">
            {user?.givenName}
          </StyledUserLabel> */}
          <ProfileIconContainer>
            {/* <IconSymbol
              icon="person"
              size="25px"
              color="$primary1"
              data-testid="profile-icon"
            /> */}
          </ProfileIconContainer>
          <TransparentAvatar>{getInitials(user?.displayName || user?.givenName)}</TransparentAvatar>
          {openLogout && (
            <div ref={dropdownRef}>
              <StyledLogoutCard isOpen={openLogout}>
                <Card
                  css={{
                    position: 'absolute',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    top: '2.5rem',
                    right: '0',
                    cursor: 'pointer',
                    zIndex: 900,
                  }}
                >
                  {isActorAdmin && (
                    <Card.Section
                      onClick={Action.Impersonation}
                      css={{
                        fontSize: '14px',
                        color: '#424242',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#0056b3',
                          textDecoration: 'underline',
                          backgroundColor: '#f5f5f5ff',
                        },
                      }}
                      role="menuitem"
                    >
                      Impersonation
                    </Card.Section>
                  )}

                  <Card.Section
                    onClick={Action.Logout}
                    css={{
                      fontSize: '14px',
                      color: '#424242',
                      cursor: 'pointer',
                      '&:hover': {
                        color: '#0056b3',
                        textDecoration: 'underline',
                        backgroundColor: '#f5f5f5ff',
                      },
                    }}
                    role="menuitem"
                  >
                    Logout
                  </Card.Section>
                </Card>
              </StyledLogoutCard>
            </div>
          )}
        </StyledUserFlex>
      </Flex>
    </Flex>
  );
};
